import { useState, useEffect, useRef, useCallback } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import {
  FaCreditCard, FaCalendarAlt, FaCheckCircle,
  FaExclamationCircle, FaSpinner, FaHistory, FaBolt, FaUniversity
} from 'react-icons/fa';
import TenantSidebar from './TenantSidebar';
import TenantNavbar from './TenantNavbar';
import LoadingScreen from '../PropertyManager/LoadingScreen';
import '../PropertyManager/Sidebar.css';
import '../PropertyManager/Navbar.css';
import './TenantPayments.css';
import Tutorial from '../PropertyManager/Tutorial';

const BASE_URL = 'https://api.rside.app';

const STRIPE_PK = 'pk_live_51Tuw5GAY3PYzTZdgXoeNSYlXylOIyCLmjr3XTHJTzTgtclgq5AEHKcDPCL1y8e0rPT5lOrTbSQ2VTX6i9cXOL7Lw00CHSypyIu';

const TP_STEPS = [
  {
    title: 'Rent Payments',
    description: 'Pay your rent securely using your credit or debit card through Stripe.',
  },
  {
    title: 'Balance Due',
    description: 'This shows your current balance. A past-due balance will appear in red.',
    anchorId: 'tp-balance-card',
    cardAlignment: 'bottom',
  },
  {
    title: 'Payment Amount',
    description: 'Choose to pay the full amount, half, or enter a custom amount.',
    anchorId: 'tp-amount-tabs',
    cardAlignment: 'bottom',
  },
  {
    title: 'Card Details',
    description: 'Enter your card information here. All payments are processed securely by Stripe.',
    anchorId: 'tp-card-section',
    cardAlignment: 'top',
  },
  {
    title: 'Make Payment',
    description: 'Tap Pay to complete your payment once you\'ve filled in your card details.',
    anchorId: 'tp-pay-btn',
    cardAlignment: 'top',
  },
];

function fmt$(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function nextDueDateObj(rentDueDay) {
  const today = new Date();
  const day = parseInt(rentDueDay) || 1;
  let due = new Date(today.getFullYear(), today.getMonth(), day);
  if (due <= today) due = new Date(today.getFullYear(), today.getMonth() + 1, day);
  return due;
}

function daysUntilNextDue(rentDueDay) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((nextDueDateObj(rentDueDay) - today) / 86400000);
}

function daysPastCurrentDue(rentDueDay) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = parseInt(rentDueDay) || 1;
  const currentDue = new Date(today.getFullYear(), today.getMonth(), day);
  return Math.round((today - currentDue) / 86400000);
}

function fmtDueDate(date) {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtDate(iso) {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function TenantPayments() {
  const navigate = useNavigate();
  const [loading, setLoading]           = useState(true);
  const [tenantName, setTenantName]     = useState('');
  const [initials, setInitials]         = useState('');
  const [sessionData, setSessionData]   = useState(null);
  const [rentData, setRentData]         = useState(null);
  const [pendingFees, setPendingFees]   = useState([]);
  const [amountType, setAmountType]     = useState('full');
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('ach'); // 'ach' | 'card'
  const [paying, setPaying]             = useState(false);
  const [payMsg, setPayMsg]             = useState({ type: '', text: '' });
  const [stripeReady, setStripeReady]     = useState(false);
  const [cardContainer, setCardContainer] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [listHistory, setListHistory] = useState([]);

  const stripeRef = useRef(null);
  const cardRef   = useRef(null);

  useEffect(() => {
    const init = () => {
      if (!stripeRef.current) stripeRef.current = window.Stripe(STRIPE_PK);
      setStripeReady(true);
    };
    if (window.Stripe) { init(); return; }
    if (!document.getElementById('stripe-js')) {
      const s = document.createElement('script');
      s.id = 'stripe-js';
      s.src = 'https://js.stripe.com/v3/';
      s.onload = init;
      document.head.appendChild(s);
    }
  }, []);

  const cardContainerRef = useCallback((node) => {
    setCardContainer(node);
  }, []);

  useEffect(() => {
    if (!cardContainer || !stripeReady || !stripeRef.current || cardRef.current || paymentMethod !== 'card') return;
    const elements = stripeRef.current.elements();
    const card = elements.create('card', {
      style: {
        base: {
          fontSize: '15px',
          fontFamily: 'inherit',
          color: '#1a1a2e',
          '::placeholder': { color: '#bbb' },
        },
        invalid: { color: '#ef4444' },
      },
    });
    card.mount(cardContainer);
    cardRef.current = card;
    return () => {
      try { card.unmount(); } catch {}
      cardRef.current = null;
    };
  }, [cardContainer, stripeReady, paymentMethod]);

  const fetchRentData = useCallback(async (doorId) => {
    const res = await fetch(`${BASE_URL}/GetPastDuePaymentsByDoor?door_id=${doorId}`);
    const data = await res.json();
    setRentData(data);
  }, []);

  const fetchListHistory = useCallback(async (doorId) => {
    try {
      const res = await fetch(`${BASE_URL}/TenantApp/ListPaymentHistory?Door_id=${doorId}`);
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data?.payments || data?.history || []);
        setListHistory(list);
      }
    } catch {}
  }, []);

  const fetchPendingFees = useCallback(async (doorId) => {
    try {
      const res = await fetch(`${BASE_URL}/GetPushedFeesByDoor?door_id=${doorId}`);
      if (res.ok) {
        const data = await res.json();
        const fees = (data.pushed_fees || []).filter(
          f => (f.Status || '').toLowerCase() === 'pending'
        );
        setPendingFees(fees);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) { navigate('/tenant/login'); return; }
      try {
        const res    = await fetch(`${BASE_URL}/TenantApp/SessionInfo?email=${encodeURIComponent(user.email)}`);
        const sd     = await res.json();
        const name   = sd?.tenant?.full_name || '';
        setTenantName(name);
        setInitials(name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2));
        setSessionData(sd);
        setUserEmail(user.email);
        if (sd?.tenant?.door_id) {
          await Promise.all([
            fetchRentData(sd.tenant.door_id),
            fetchPendingFees(sd.tenant.door_id),
            fetchListHistory(sd.tenant.door_id),
          ]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate, fetchRentData, fetchPendingFees, fetchListHistory]);

  if (loading) return <LoadingScreen />;

  const tenant      = sessionData?.tenant;
  const tenantId    = tenant?.id ?? tenant?.tenant_id ?? null;
  const doorId      = tenant?.door_id;
  const summary     = rentData?.summary;
  const lease       = rentData?.lease;
  const history     = rentData?.rent_history || [];
  const monthlyRent = Number(summary?.monthly_rent || lease?.Rent || 0);
  const rentDueDay  = lease?.Rent_Due || 1;
  const getListDate = (p) => p.paydate || p.Paydate || p.Pay_Date || p.pay_date || p.Payment_Date || p.payment_date || p.date_paid || p.DatePaid || p.Date || p.date || p.Created_At || p.created_at || '';
  const recentSource = listHistory.length > 0 ? listHistory : [...history].reverse();
  const recentPayments = [...recentSource].sort((a, b) => new Date(getListDate(b)) - new Date(getListDate(a))).slice(0, 3);

  // Fee totals
  const pushedFeesTotal = pendingFees.reduce((s, f) => s + Number(f.Amount || 0), 0);
  const apiPastDue      = Number(summary?.past_due ?? 0);
  const withinThreeDays = daysUntilNextDue(rentDueDay) <= 3;
  const hasRentComponent = apiPastDue > pushedFeesTotal;

  // Total due
  let totalDue;
  if (hasRentComponent) {
    totalDue = apiPastDue;
  } else if (withinThreeDays) {
    totalDue = monthlyRent + pushedFeesTotal;
  } else if (pushedFeesTotal > 0) {
    totalDue = pushedFeesTotal;
  } else {
    totalDue = 0;
  }

  // Due date label
  let dueDateStr = null;
  if (totalDue > 0) {
    if (hasRentComponent) {
      const currentDue = new Date(new Date().getFullYear(), new Date().getMonth(), rentDueDay);
      dueDateStr = fmtDueDate(currentDue);
    } else {
      dueDateStr = fmtDueDate(nextDueDateObj(rentDueDay));
    }
  }

  // Badge
  const pastDays = daysPastCurrentDue(rentDueDay);
  const badgePill = totalDue === 0 ? null
    : (hasRentComponent && pastDays >= 5) ? 'PAST DUE'
    : 'UPCOMING';

  // Rent portion for breakdown display
  const rentPortion = Math.max(0, totalDue - pushedFeesTotal);
  const hasRent = rentPortion > 0;

  // Minimum payment: any amount if rent exists; smallest fee if fees-only
  const minPayment = hasRent
    ? 0.01
    : (pendingFees.length > 0 ? Math.min(...pendingFees.map(f => Number(f.Amount || 0))) : 0.01);

  const displayAmount =
    amountType === 'full'   ? totalDue
    : amountType === 'half' ? Math.round((totalDue / 2) * 100) / 100
    : parseFloat(customAmount) || 0;

  // Processing fees
  const achFee  = displayAmount > 0 ? Math.min(Math.round(displayAmount * 0.008 * 100) / 100, 5) : 0;
  const cardFee = displayAmount > 0 ? Math.round((displayAmount * 0.029 + 0.30) * 100) / 100 : 0;
  const processingFee = paymentMethod === 'ach' ? achFee : cardFee;
  const totalCharged  = displayAmount + processingFee;

  // Record fees + rent after successful Stripe charge
  const recordPayments = async (paidAmount) => {
    const today = new Date().toISOString().split('T')[0];
    let remaining = paidAmount;

    // Sort pending fees oldest first
    const sorted = [...pendingFees].sort(
      (a, b) => new Date(a.Create_Date) - new Date(b.Create_Date)
    );

    for (const fee of sorted) {
      const feeAmt = Number(fee.Amount || 0);
      if (remaining >= feeAmt) {
        const feeParams = new URLSearchParams({
          door_id:         doorId,
          amount:          feeAmt,
          paydate:         today,
          method:          'Stripe',
          Source:          'App',
          Category:        'Fee',
          partial_payment: 0,
          fees_added:      feeAmt,
          notes:           fee.Fee_Type || 'Fee',
        });
        await fetch(`${BASE_URL}/AddRent?${feeParams}`, {
          method: 'POST',
          headers: { accept: 'application/json' },
        });
        await fetch(
          `${BASE_URL}/UpdatePushedFeeStatus?pushed_fee_id=${fee.id}&status=Paid`,
          { method: 'PUT' }
        );
        remaining -= feeAmt;
      }
      // if can't afford this fee, skip and check next
    }

    // Record any remaining amount as rent
    if (remaining > 0) {
      const isPartial = remaining < monthlyRent;
      const rentParams = new URLSearchParams({
        door_id:         doorId,
        amount:          remaining,
        paydate:         today,
        method:          'Stripe',
        Source:          'App',
        Category:        'Rent',
        partial_payment: isPartial ? 1 : 0,
        fees_added:      0,
        month_of:        new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      });
      await fetch(`${BASE_URL}/AddRent?${rentParams}`, {
        method: 'POST',
        headers: { accept: 'application/json' },
      });
    }
  };

  const handlePay = async () => {
    if (!stripeRef.current) {
      setPayMsg({ type: 'error', text: 'Payment not ready. Please refresh and try again.' });
      return;
    }
    if (paymentMethod === 'card' && !cardRef.current) {
      setPayMsg({ type: 'error', text: 'Card element not ready. Please refresh.' });
      return;
    }
    if (displayAmount <= 0) {
      setPayMsg({ type: 'error', text: 'Please enter a valid amount.' });
      return;
    }
    if (displayAmount < minPayment) {
      setPayMsg({ type: 'error', text: `Minimum payment is ${fmt$(minPayment)}.` });
      return;
    }
    if (!tenantId) {
      setPayMsg({ type: 'error', text: 'Tenant ID not found. Please contact support.' });
      return;
    }
    setPaying(true);
    setPayMsg({ type: '', text: '' });
    try {
      const totalCharged = paymentMethod === 'ach'
        ? displayAmount + achFee
        : displayAmount + cardFee;
      const amountCents = Math.round(totalCharged * 100);

      await fetch(`${BASE_URL}/Stripe/CreateTenantCustomer?tenant_id=${tenantId}`, { method: 'POST' }).catch(() => {});

      const intentRes = await fetch(
        `${BASE_URL}/Stripe/CreateRentPayment?tenant_id=${tenantId}&door_id=${doorId}&amount_cents=${amountCents}&payment_method_type=${paymentMethod === 'ach' ? 'us_bank_account' : 'card'}`,
        { method: 'POST' }
      );
      if (!intentRes.ok) throw new Error('Failed to initiate payment. Please try again.');
      const intentRaw    = await intentRes.json();
      const clientSecret = typeof intentRaw === 'string'
        ? intentRaw
        : (intentRaw?.client_secret ?? intentRaw?.clientSecret ?? null);
      if (!clientSecret) throw new Error('No payment token received from server.');

      if (paymentMethod === 'ach') {
        // Step 1: open Stripe Financial Connections to collect bank account
        const { paymentIntent: pi1, error: collectError } = await stripeRef.current.collectBankAccountForPayment({
          clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: { name: tenantName, email: userEmail },
            },
          },
          expand: ['payment_method'],
        });
        if (collectError) throw new Error(collectError.message);
        if (!pi1 || pi1.status === 'requires_payment_method') {
          throw new Error('Bank account collection was cancelled.');
        }

        // Step 2: confirm the payment (triggers mandate acceptance + submission)
        if (pi1.status === 'requires_confirmation') {
          const { error: confirmError } = await stripeRef.current.confirmUsBankAccountPayment(clientSecret);
          if (confirmError) throw new Error(confirmError.message);
        }

        await recordPayments(displayAmount);
        setPayMsg({ type: 'success', text: `Payment of ${fmt$(displayAmount)} submitted! ACH takes 2–3 business days to process.` });
      } else {
        const { paymentIntent, error } = await stripeRef.current.confirmCardPayment(clientSecret, {
          payment_method: { card: cardRef.current },
        });
        if (error) throw new Error(error.message);
        if (paymentIntent?.status !== 'succeeded') throw new Error('Payment was not completed. Please try again.');
        await recordPayments(displayAmount);
        setPayMsg({ type: 'success', text: `Payment of ${fmt$(displayAmount)} was successful!` });
      }

      setAmountType('full');
      setCustomAmount('');
      if (doorId) {
        await Promise.all([fetchRentData(doorId), fetchPendingFees(doorId)]);
      }
    } catch (err) {
      setPayMsg({ type: 'error', text: err.message || 'Payment failed. Please try again.' });
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="home-page">
      <TenantSidebar initials={initials} />
      <div className="main-content">
        <TenantNavbar title="Payments" tenantName={tenantName} initials={initials} />

        <div className="tp-body">
          <div className="tp-dashboard">

            {/* ── LEFT: Balance + Fees + Recent ── */}
            <div className="tp-dash-left">

              <div id="tp-balance-card" className="tp-balance-card">
                <div className="tp-balance-top">
                  <span className="tp-balance-label">{totalDue === 0 ? 'ALL PAID' : 'BALANCE DUE'}</span>
                  {badgePill && (
                    <span className={badgePill === 'PAST DUE' ? 'tp-pastdue-badge' : 'tp-upcoming-badge'}>
                      {badgePill}
                    </span>
                  )}
                </div>
                <div className="tp-balance-amount">{fmt$(totalDue)}</div>
                <div className="tp-balance-meta">
                  {dueDateStr && (
                    <div className="tp-balance-row">
                      <FaCalendarAlt style={{ opacity: 0.85 }} />
                      <span>Due {dueDateStr}</span>
                    </div>
                  )}
                  {monthlyRent > 0 && (
                    <div className="tp-balance-row">
                      <FaCreditCard style={{ opacity: 0.85 }} />
                      <span>Monthly rent: {fmt$(monthlyRent)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Pending Fees Breakdown */}
              {pendingFees.length > 0 && (
                <div className="tp-fees-card">
                  <div className="tp-fees-hd">
                    <FaBolt style={{ color: '#2563eb', fontSize: 12 }} />
                    <span className="tp-section-label">FEES INCLUDED IN TOTAL</span>
                  </div>
                  <div className="tp-fees-list">
                    {pendingFees.map((fee, i) => (
                      <div key={fee.id || i} className="tp-fee-row">
                        <span className="tp-fee-name">{fee.Fee_Type || 'Fee'}</span>
                        <span className="tp-fee-amt">{fmt$(fee.Amount)}</span>
                      </div>
                    ))}
                    {pushedFeesTotal > 0 && rentPortion > 0 && (
                      <>
                        <div className="tp-fee-divider" />
                        <div className="tp-fee-row tp-fee-row-sub">
                          <span className="tp-fee-name">Rent</span>
                          <span className="tp-fee-amt">{fmt$(rentPortion)}</span>
                        </div>
                        <div className="tp-fee-row tp-fee-total">
                          <span className="tp-fee-name">Total</span>
                          <span className="tp-fee-amt">{fmt$(totalDue)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {recentPayments.length > 0 && (
                <div className="tp-recent-card">
                  <div className="tp-recent-hd">
                    <span className="tp-section-label">RECENT PAYMENTS</span>
                    <button className="tp-history-btn" onClick={() => navigate('/tenant/payment-history')}>
                      <FaHistory style={{ fontSize: 11 }} /> View all
                    </button>
                  </div>
                  <div className="tp-recent-cols-hd">
                    <span />
                    <span>Date</span>
                    <span>Method</span>
                    <span>Amount</span>
                    <span>Fees</span>
                  </div>
                  {recentPayments.map((p, i) => {
                    const fees = Number(p.Fees ?? p.fees_added ?? p.Fee ?? p.fee ?? 0);
                    const isFee = (p.category || p.Category || '').toLowerCase() === 'fee';
                    const method = p.Method || p.method || '—';
                    return (
                      <div key={p.id || i} className="tp-recent-row tp-recent-row-cols">
                        <div className="tp-recent-icon"><FaCheckCircle /></div>
                        <span className="tp-recent-col-date">{fmtDate(getListDate(p))}</span>
                        <span>{method}</span>
                        <span className="tp-recent-amount">{fmt$(p.Amount || p.amount || 0)}</span>
                        <span className={`tp-recent-fees ${fees > 0 || isFee ? 'tp-recent-fees-val' : ''}`}>
                          {fees > 0 ? fmt$(fees) : isFee ? <span className="tp-fee-badge">Fee</span> : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

            </div>

            {/* ── RIGHT: Payment form ── */}
            <div className="tp-dash-right">
              <div className="tp-pay-card">
                <span className="tp-section-label">PAYMENT AMOUNT</span>

                <div id="tp-amount-tabs" className="tp-amount-tabs">
                  <button
                    className={`tp-tab ${amountType === 'full' ? 'tp-tab-active' : ''}`}
                    onClick={() => setAmountType('full')}
                  >
                    <span className="tp-tab-main">Full</span>
                    <span className="tp-tab-sub">{fmt$(totalDue)}</span>
                  </button>
                  <button
                    className={`tp-tab ${amountType === 'half' ? 'tp-tab-active' : ''}`}
                    onClick={() => setAmountType('half')}
                  >
                    <span className="tp-tab-main">Half</span>
                    <span className="tp-tab-sub">{fmt$(totalDue / 2)}</span>
                  </button>
                  <button
                    className={`tp-tab ${amountType === 'custom' ? 'tp-tab-active' : ''}`}
                    onClick={() => setAmountType('custom')}
                  >
                    <span className="tp-tab-main">Custom</span>
                  </button>
                </div>

                {amountType === 'custom' && (
                  <div className="tp-custom-wrap">
                    <span className="tp-dollar-sign">$</span>
                    <input
                      className="tp-custom-input"
                      type="number"
                      min={minPayment}
                      step="0.01"
                      placeholder="0.00"
                      value={customAmount}
                      onChange={e => setCustomAmount(e.target.value)}
                    />
                    {!hasRent && pendingFees.length > 0 && (
                      <div className="tp-min-hint">Minimum: {fmt$(minPayment)}</div>
                    )}
                  </div>
                )}

                {/* Payment method toggle */}
                <div id="tp-card-section" className="tp-card-section">
                  <span className="tp-section-label">PAYMENT METHOD</span>
                  <div className="tp-method-tabs">
                    <button
                      className={`tp-method-tab ${paymentMethod === 'ach' ? 'tp-method-tab-active' : ''}`}
                      onClick={() => { setPaymentMethod('ach'); setPayMsg({ type: '', text: '' }); }}
                    >
                      <FaUniversity /> Bank (ACH)
                    </button>
                    <button
                      className={`tp-method-tab ${paymentMethod === 'card' ? 'tp-method-tab-active' : ''}`}
                      onClick={() => { setPaymentMethod('card'); setPayMsg({ type: '', text: '' }); }}
                    >
                      <FaCreditCard /> Card
                    </button>
                  </div>

                  {/* Fee breakdown */}
                  {displayAmount > 0 && (
                    <div className="tp-fee-breakdown">
                      <div className="tp-fee-breakdown-row">
                        <span>Subtotal</span>
                        <span>{fmt$(displayAmount)}</span>
                      </div>
                      <div className="tp-fee-breakdown-row">
                        <span>Processing fee</span>
                        <span>{fmt$(processingFee)}</span>
                      </div>
                      <div className="tp-fee-breakdown-row tp-fee-breakdown-total">
                        <span>Total</span>
                        <span>{fmt$(totalCharged)}</span>
                      </div>
                      {paymentMethod === 'ach'
                        ? <p className="tp-ach-note">ACH payments have lower fees (0.8%, max $5)</p>
                        : <p className="tp-card-note">Card processing fee: 2.9% + $0.30</p>
                      }
                    </div>
                  )}

                  {/* Card element — only shown for card method */}
                  {paymentMethod === 'card' && (
                    <div className="tp-stripe-wrap" style={{marginTop:8}}>
                      <div ref={cardContainerRef} />
                    </div>
                  )}
                </div>

                {payMsg.text && (
                  <div className={`tp-pay-msg ${payMsg.type === 'success' ? 'tp-pay-msg-ok' : 'tp-pay-msg-err'}`}>
                    {payMsg.type === 'success'
                      ? <FaCheckCircle style={{ flexShrink: 0 }} />
                      : <FaExclamationCircle style={{ flexShrink: 0 }} />}
                    {payMsg.text}
                  </div>
                )}

                <button id="tp-pay-btn" className="tp-pay-btn" onClick={handlePay} disabled={paying || displayAmount <= 0}>
                  {paying
                    ? <><FaSpinner className="tp-spin" /> Processing…</>
                    : paymentMethod === 'ach'
                      ? <><FaUniversity /> Pay {fmt$(totalCharged)}</>
                      : <><FaCreditCard /> Pay {fmt$(totalCharged)}</>}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
      <Tutorial
        steps={TP_STEPS}
        storageKey={`hasSeenTenantPaymentsTutorial_${userEmail}`}
      />
    </div>
  );
}

export default TenantPayments;
